import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * English (the default) and Arabic, with full RTL — the portal's own short
 * dictionary. Arabic flips `dir` on <html>, which drives every logical-property
 * utility in the layout. The dictionary is flat and typed, so a missing key is a
 * compile error rather than a blank string in production.
 */

export type Locale = 'en' | 'ar';

const en = {
  // Shell
  'app.name': 'Viganium Client Portal',
  'app.tagline': 'Your tickets, tasks and change requests',
  'app.surface': 'Client portal',
  'nav.dashboard': 'Dashboard',
  'nav.tickets': 'Tickets',
  'nav.changeRequests': 'Change requests',
  'nav.complaints': 'Complaints',
  'nav.account': 'Account',
  'nav.openMenu': 'Open the menu',
  'theme.toLight': 'Switch to the light theme',
  'theme.toDark': 'Switch to the dark theme',

  // Common
  'common.search': 'Search',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.create': 'Create',
  'common.send': 'Send',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.loading': 'Loading…',
  'common.noResults': 'Nothing to show',
  'common.noResultsHint': 'Try another tab or search.',
  'common.error': 'Something went wrong',
  'common.retry': 'Try again',
  'common.total': 'Total',
  'common.page': 'Page',
  'common.of': 'of',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.all': 'All',
  'common.optional': 'Optional',
  'common.noProject': 'No particular project',
  'common.project': 'Project',
  'common.status': 'Status',
  'common.priority': 'Priority',
  'common.nameTooLong': 'Keep it to {max} characters — this is {count}.',
  'common.created': 'Raised',
  'common.updated': 'Updated',
  'common.email': 'Email',
  'common.mobile': 'Mobile',
  'common.you': 'You',
  'common.viganium': 'Viganium',

  // Signing in
  'auth.welcome': 'Sign in',
  'auth.subtitle': 'Follow your tickets, your projects’ tasks and your change requests with Viganium.',
  'auth.usernameLabel': 'Username',
  'auth.passwordLabel': 'Password',
  'auth.signIn': 'Sign in',
  'auth.signingIn': 'Signing in…',
  'auth.signOut': 'Sign out',
  'auth.forgot': 'Forgot your password? Ask your Viganium contact to set a new one.',
  'auth.disabled': 'This account is switched off. Please contact Viganium.',
  'auth.invalid': 'Incorrect username or password.',

  // Dashboard
  'dashboard.title': 'Dashboard',
  'dashboard.greeting': 'Welcome, {name}',
  'dashboard.subtitle': 'Where your tickets, tasks and change requests stand today.',
  'dashboard.ticketsTotal': 'Total tickets',
  'dashboard.ticketsOpen': 'Waiting for us',
  'dashboard.ticketsInProgress': 'In progress',
  'dashboard.ticketsFinished': 'Finished',
  'dashboard.tasks': 'Tasks on your projects',
  'dashboard.tasksHint': 'Every task on your projects, by where it stands.',
  'dashboard.tasksNone': 'No tasks on your projects yet.',
  'dashboard.projects': 'Your projects',
  'dashboard.tasksDone': 'Tasks done',
  'dashboard.team': 'Online now',
  'dashboard.teamHint': 'The people on your projects who are on shift right now.',
  'dashboard.teamEmpty': 'Nobody from your project team is on shift right now.',
  'dashboard.onBreak': 'On a break',
  'dashboard.onShiftSince': 'On shift since {time}',
  'dashboard.changeRequests': 'Change requests',
  'dashboard.changeRequestsOpen': 'Still open',
  'dashboard.seeAll': 'See all',
  'dashboard.ticketsShut': 'Raising tickets here is not open yet. Please contact Viganium.',

  // Tickets
  'tickets.title': 'Tickets',
  'tickets.new': 'New ticket',
  'tickets.number': 'Ticket',
  'tickets.subject': 'Subject',
  'tickets.subjectHint': 'A short summary — "The login page is slow".',
  'tickets.description': 'Details',
  'tickets.descriptionHint': 'What happened, where, and since when. Screenshots and voice notes help.',
  'tickets.projectHint': 'The project it is about, if it is about one.',
  // A project takes tickets while it is under way or in its support period (2026-10-09).
  'tickets.projectHintSupport':
    'The project it is about, if it is about one. A project whose support period has ended is not listed: please contact Viganium to renew it.',
  'tickets.projectClosed':
    'That project’s support period has ended, so no new tickets can be raised on it. Please contact Viganium to renew it.',
  'tickets.projectCancelled': 'That project was cancelled, so no new tickets can be raised on it.',
  'dashboard.supportUntil': 'Support until {date}',
  'tickets.replies': 'Replies',
  'tickets.groupOpen': 'Waiting for us',
  'tickets.groupInProgress': 'In progress',
  'tickets.groupFinished': 'Finished',
  'tickets.groupCancelled': 'Cancelled',
  'tickets.raisedByYou': 'Raised by you',
  'tickets.raisedByUs': 'Raised by Viganium',
  'tickets.raisedByUsFor': 'Raised by Viganium for {name}',
  'tickets.raisedBy': 'Raised by',
  'tickets.requestedBy': 'At the request of',
  'tickets.raisedByName': 'Raised by {name}',
  'tickets.department': 'Department',
  'tickets.departmentHint': 'Which of our teams your ticket goes to.',
  'tickets.departmentChoose': '— Choose a department —',
  'tickets.departmentGone': 'That department no longer takes your tickets. Please choose another.',
  'tickets.empty': 'No tickets yet',
  'tickets.emptyHint': 'Raise a ticket and our team will pick it up.',
  'tickets.created': 'Ticket {number} raised — our team has been told.',
  'tickets.handledBy': 'Handled by',
  'tickets.notAssigned': 'Not taken yet',
  'tickets.details': 'Details',
  'tickets.files': 'Files',
  'tickets.conversation': 'Conversation',
  'tickets.noReplies': 'No replies yet — our answer will appear here.',
  'tickets.answer': 'Your answer',
  'tickets.answerPlaceholder': 'Write your answer…',
  'tickets.waitingOnYou': 'We are waiting for your answer.',
  'tickets.finished': 'This ticket is finished. Raise a new ticket if you need more help.',
  'tickets.resolution': 'Resolution',
  'tickets.sent': 'Sent.',

  // Change requests
  'crs.title': 'Change requests',
  'crs.new': 'New change request',
  'crs.number': 'Request',
  'crs.titleField': 'Title',
  'crs.titleHint': 'What should change, in a few words.',
  'crs.details': 'Details',
  'crs.detailsHint': 'Describe the change. A file or a voice note can say it instead.',
  'crs.projectHint': 'The project the change is about, if it is about one.',
  'crs.empty': 'No change requests yet',
  'crs.emptyHint': 'Ask for a change and our team will review it.',
  'crs.created': 'Change request {number} sent — our team will review it.',
  'crs.ourNote': 'Our note',
  'crs.history': 'History',
  'crs.raised': 'You raised it',
  'crs.raisedByName': '{name} raised it',
  'crs.raisedBy': 'Raised by',
  'crs.files': 'Files',
  'crs.needsSomething': 'Describe the change, or attach a file or a voice note.',
  'crs.state.NEW': 'Waiting for our review.',
  'crs.state.IN_REVIEW': 'We are reviewing it.',
  'crs.state.APPROVED': 'Approved — we will carry it out.',
  'crs.state.REJECTED': 'Not going ahead — see our note.',
  'crs.state.DONE': 'Done.',
  'crs.messages': 'Messages',
  'crs.conversation': 'Conversation',
  'crs.noMessages': 'No messages yet. Write to us here about this change — our answers appear here too.',
  'crs.message': 'Your message',
  'crs.messagePlaceholder': 'Write to us about this change…',
  'crs.closed': 'This change request is done. Raise a new one if you need anything more.',

  // Complaints about someone on the client's projects (2026-10-28)
  'complaints.title': 'Complaints',
  'complaints.subtitle': 'Tell us when someone on your projects lets you down.',
  'complaints.new': 'New complaint',
  'complaints.number': 'Complaint',
  'complaints.about': 'About',
  'complaints.aboutHint': 'The people who work on your projects.',
  'complaints.find': 'Find someone…',
  'complaints.noPeople': 'Nobody works on your projects yet, so there is no one to choose.',
  'complaints.noMatch': 'Nobody matches that name.',
  'complaints.titleField': 'Title',
  'complaints.titleHint': 'What went wrong, in a few words.',
  'complaints.details': 'Details',
  'complaints.detailsHint': 'What happened, and when. A file or a voice note can say it instead.',
  'complaints.needsSomething': 'Say what happened, or attach a file or a voice note.',
  'complaints.private': 'Only Viganium’s management reads your complaint — never the person it is about.',
  'complaints.empty': 'No complaints',
  'complaints.emptyHint': 'We hope it stays that way. If someone on your projects lets you down, tell us here.',
  'complaints.created': 'Complaint {number} sent — our management will look into it.',
  'complaints.ourAnswer': 'Our answer',
  'complaints.ourNote': 'Our note',
  'complaints.history': 'History',
  'complaints.made': 'You made it',
  'complaints.madeByName': '{name} made it',
  'complaints.madeBy': 'Made by',
  'complaints.files': 'Files',
  'complaints.formerMember': 'Someone no longer with us',
  'complaints.state.NEW': 'Waiting for our management to look into it.',
  'complaints.state.IN_REVIEW': 'Our management is looking into it.',
  'complaints.state.CLOSED': 'Closed — see our answer.',

  // Account
  'account.title': 'Account',
  'account.company': 'Company',
  'account.signedInAs': 'Signed in as',
  'account.companySignIn': 'The company’s own sign-in',
  'account.username': 'Username',
  'account.lastSignIn': 'Signed in before',
  'account.changePassword': 'Change password',
  'account.currentPassword': 'Current password',
  'account.newPassword': 'New password',
  'account.confirmPassword': 'New password, again',
  'account.passwordHint': 'At least 8 characters.',
  'account.mismatch': 'The two new passwords are not the same.',
  'account.changed': 'Your password has been changed. Other devices are signed out.',

  // Statuses
  'status.ticket.NEW': 'New',
  'status.ticket.ASSIGNED': 'Taken',
  'status.ticket.IN_PROGRESS': 'In progress',
  'status.ticket.WAITING_REQUESTER': 'Waiting on you',
  'status.ticket.RESOLVED': 'Resolved',
  'status.ticket.CLOSED': 'Closed',
  'status.ticket.REOPENED': 'Reopened',
  'status.ticket.CANCELLED': 'Cancelled',
  'status.task.TO_DO': 'To do',
  'status.task.IN_PROGRESS': 'In progress',
  'status.task.BLOCKED': 'Blocked',
  'status.task.REVIEW': 'In review',
  'status.task.DONE': 'Done',
  'status.task.CANCELLED': 'Cancelled',
  'status.cr.NEW': 'New',
  'status.cr.IN_REVIEW': 'In review',
  'status.cr.APPROVED': 'Approved',
  'status.cr.REJECTED': 'Rejected',
  'status.cr.DONE': 'Done',
  'status.complaint.NEW': 'New',
  'status.complaint.IN_REVIEW': 'In review',
  'status.complaint.CLOSED': 'Closed',
  'status.project.DRAFT': 'Planning',
  'status.project.ACTIVE': 'Active',
  'status.project.TESTING': 'Testing',
  'status.project.ON_HOLD': 'On hold',
  'status.project.SUPPORT': 'In support',
  'status.project.COMPLETED': 'Completed',
  'status.project.CANCELLED': 'Cancelled',
  'status.project.ARCHIVED': 'Archived',
  'priority.LOW': 'Low',
  'priority.MEDIUM': 'Medium',
  'priority.HIGH': 'High',
  'priority.URGENT': 'Urgent',

  // Attachments — the composer and the file list, as in the admin
  'attachments.title': 'Attachments',
  'attachments.none': 'No files yet.',
  'attachments.attachFiles': 'Attach files',
  'attachments.recordVoice': 'Voice note',
  'attachments.recordVoiceHint': 'Record a voice note',
  'attachments.recording': 'Recording',
  'attachments.startingMic': 'Starting the microphone…',
  'attachments.stopRecording': 'Stop and keep',
  'attachments.discardRecording': 'Discard',
  'attachments.voiceNote': 'Voice note',
  'attachments.hint': 'Pictures, PDF, Word, Excel, voice notes and videos — up to {mb} MB a file, {videoMb} MB a video.',
  'attachments.dropHere': 'Drop files to attach them',
  'attachments.attach': 'Attach',
  'attachments.uploading': 'Uploading…',
  'attachments.remove': 'Remove',
  'attachments.removeConfirm': 'Remove this file?',
  'attachments.keep': 'Keep',
  'attachments.removed': 'File removed.',
  'attachments.added': 'Files attached.',
  'attachments.download': 'Download',
  'attachments.open': 'Open',
  'attachments.play': 'Play',
  'attachments.pause': 'Pause',
  'attachments.speed': 'Playback speed',
  'attachments.micDenied': 'Allow the microphone in your browser to record a voice note.',
  'attachments.micUnavailable': 'This browser or device cannot record audio.',
  'attachments.tooLarge': 'Larger than {mb} MB.',
  'attachments.typeNotAllowed': 'This type of file is not accepted.',
  'attachments.tooMany': 'Up to {n} files at a time.',
  'attachments.maxLength': 'A voice note stops at {min} minutes.',
  'attachments.playFailed': 'This recording cannot be played here — download it instead.',
  'attachments.failed': 'Upload failed.',
  'attachments.waitForUploads': 'Wait for the uploads to finish.',
  'attachments.addedBy': 'Added by {name}',
  'attachments.tooLargeVideo': 'Videos can be up to {mb} MB.',
  'attachments.videoUnplayable': 'This video cannot play here — download it instead.',
  'attachments.storageUnreachable': 'The file storage could not be reached. Try again in a moment.',
  'attachments.notArrived': 'The file did not arrive whole. Remove it and attach it again.',

  // Not found
  'notFound.title': 'This page does not exist',
  'notFound.hint': 'The link may be old. Start again from the dashboard.',
  // Calls (2026-11-01): the client calls the people Viganium chose for it.
  'nav.calls': 'Calls',
  'calls.title': 'Calls',
  'calls.subtitle': 'Call the Viganium team members chosen for you. Every call is recorded.',
  'calls.unavailable': 'Calls are not available right now. Please try again later.',
  'calls.whoYouCanCall': 'Who you can call',
  'calls.whoYouCanCallHint': 'The Viganium team members chosen for you, and whether each is working now.',
  'calls.nobody': 'Nobody to call yet',
  'calls.nobodyHint': 'Viganium has not chosen anyone for you to call yet.',
  'calls.recordedNotice': 'This call is recorded',
  'calls.recordingNow': 'This call is being recorded',
  'calls.recordingBadge': 'Recording',
  'calls.person.working': 'Working now',
  'calls.person.onBreak': 'On a break',
  'calls.person.notWorking': 'Not working now',
  'calls.person.onCall': 'On another call',
  'calls.call': 'Call',
  'calls.callPerson': 'Call {name}',
  'calls.yourCalls': 'Your calls',
  'calls.yourCallsHint': 'Every call made from your company’s sign-ins.',
  'calls.filter.all': 'All',
  'calls.filter.answered': 'Answered',
  'calls.filter.unanswered': 'Not answered',
  'calls.noneYet': 'No calls yet',
  'calls.noneYetHint': 'Your calls show here once you make one.',
  'calls.with': 'With',
  'calls.outcome': 'Outcome',
  'calls.madeBy': 'Made by',
  'calls.when': 'When',
  'calls.duration': 'Duration',
  'calls.voiceCall': 'Voice call',
  'calls.groupCall': 'Group call',
  'calls.loadMore': 'Load more',
  'calls.you': 'You',
  'calls.notFound': 'There is no such call.',
  'calls.people': 'People',
  'calls.peopleHint': 'Who was called, and who was on the call.',
  'calls.wasOnCall': 'On the call',
  'calls.wasRecorded': 'This call was recorded by Viganium.',
  'calls.details': 'Details',
  'calls.status': 'Status',
  'calls.madeAt': 'Made',
  'calls.started': 'Started',
  'calls.ended': 'Ended',
  'calls.why': 'Why it ended',
  'calls.addPeople': 'Add people',
  'calls.addPeopleSubtitle': 'They are asked into this same call — the same recording.',
  'calls.whoToAdd': 'Who to add',
  'calls.add': 'Add to the call',
  'calls.added': 'Asked into the call.',
  'calls.notInCall': 'You are not in a call.',
  'calls.nobodyToAdd': 'Everyone you can call is on this call already.',
  'calls.upTo': 'Up to {n} more people.',
  'calls.currentCall': 'Your call',
  'calls.move': 'Move the call',
  'calls.moveHint': 'Drag it anywhere, or use the arrow keys. Double-click puts it back in the corner.',
  'calls.state.calling': 'Calling…',
  'calls.state.preparing': 'Starting the recording…',
  'calls.state.connecting': 'Connecting…',
  'calls.state.reconnecting': 'Reconnecting…',
  'calls.state.ending': 'Ending…',
  'calls.state.otherDevice': 'Made from another tab or device',
  'calls.peopleCount': '{n} people',
  'calls.endingSoon': 'This call ends in 5 minutes — the longest a call may last.',
  'calls.micDenied': 'Your browser blocks the microphone: allow it in the address bar to be heard.',
  'calls.enableAudio': 'Click to hear the call',
  'calls.inCall': 'In the call',
  'calls.joining': 'Joining…',
  'calls.mute': 'Mute',
  'calls.unmute': 'Unmute',
  'calls.share.start': 'Share your screen',
  'calls.share.stop': 'Stop sharing your screen',
  'calls.share.yours': 'You are sharing your screen',
  'calls.share.failed': 'Your screen could not be shared. Try again, or pick another window or tab.',
  'calls.screen.label': 'Shared screens',
  'calls.screen.of': '{name}’s screen',
  'calls.screen.show': 'Show {name}’s screen',
  'calls.screen.smaller': 'Make it smaller',
  'calls.screen.larger': 'Make it larger',
  'calls.screen.full': 'Full screen',
  'calls.screen.exitFull': 'Leave full screen',
  'calls.screen.hide': 'Put it away',
  'calls.screen.float': 'Float it over other windows',
  'calls.screen.move': 'Move the shared screen',
  'calls.screen.moveHint': 'Drag it anywhere by its top or its picture, or use the arrow keys. Double-click puts it back.',
  'calls.screen.resize': 'Resize the shared screen',
  'calls.screen.resizeHint': 'Drag this corner, or use the arrow keys.',
  'calls.screen.notRecorded': 'The call’s sound is recorded — the shared screen is not.',
  'calls.screen.waiting': 'Waiting for the picture…',
  'calls.hangUp': 'Hang up',
  'calls.cancel': 'Cancel',
  'calls.openCall': 'Open the call',
  'calls.outcome.LIVE': 'In progress',
  'calls.outcome.ANSWERED': 'Answered',
  'calls.outcome.NO_ANSWER': 'No answer',
  'calls.outcome.MISSED': 'Missed',
  'calls.outcome.DECLINED': 'Declined',
  'calls.outcome.BUSY': 'Busy',
  'calls.outcome.CANCELLED': 'Cancelled',
  'calls.outcome.FAILED': 'Failed',
  'calls.participant.RINGING': 'Ringing',
  'calls.participant.ACCEPTED': 'Answered',
  'calls.participant.DECLINED': 'Declined',
  'calls.participant.MISSED': 'No answer',
  'calls.participant.BUSY': 'Busy',
  'calls.participant.CANCELLED': 'Called off',
  'calls.participant.LEFT': 'Left',
  'calls.callStatus.RINGING': 'Ringing',
  'calls.callStatus.PREPARING': 'Starting',
  'calls.callStatus.ACTIVE': 'In progress',
  'calls.callStatus.ENDING': 'Ending',
  'calls.callStatus.COMPLETED': 'Completed',
  'calls.callStatus.MISSED': 'No answer',
  'calls.callStatus.REJECTED': 'Declined',
  'calls.callStatus.CANCELLED': 'Cancelled',
  'calls.callStatus.BUSY': 'Busy',
  'calls.callStatus.FAILED': 'Failed',
  'calls.ended.normal': 'The call ended.',
  'calls.ended.noAnswer': 'Nobody answered the call.',
  'calls.ended.declined': 'The call was declined.',
  'calls.ended.busy': 'They are on another call. Please try again later.',
  'calls.ended.cancelled': 'The call was called off.',
  'calls.ended.recordingNotStarted': 'The call could not start: its recording did not start, and no call goes on without one.',
  'calls.ended.recordingLost': 'The call ended because its recording could not be confirmed.',
  'calls.ended.maxDuration': 'The call reached the longest a call may last.',
  'calls.ended.lostServer': 'The connection to Viganium was lost, so the call was ended: its recording could not be confirmed.',
  'calls.ended.lostServerRinging': 'The connection to Viganium was lost while the call was ringing, so it was called off.',
  'calls.error.micBlocked': 'Your browser blocks the microphone. Allow it in the address bar, then call again.',
  'calls.error.micMissing': 'No microphone could be opened. Plug one in, or close what is using it, then call again.',
  'calls.error.unavailable': 'Calls are not available right now. Please try again later.',
  'calls.error.alreadyInCall': 'You are in a call already. Hang up first.',
  'calls.error.full': 'A call holds {n} people at most.',
  'calls.error.otherDevice': 'This call was made from another tab or device.',
  'calls.error.notCallable': 'You can call only the people Viganium has chosen for you.',
  'calls.error.notAvailable': 'This person cannot take calls right now.',
  // A Viganium Drive link's page (2026-11-02): pages/Share.tsx.
  'share.kicker': 'Shared with you',
  'share.from': '{name} at {company} shared this with you.',
  'share.forYou': 'For {name}',
  'share.until': 'Available until {date}',
  'share.download': 'Download',
  // A folder downloaded as one .zip (2026-11-03).
  'share.downloadAll': 'Download all',
  'share.downloadAllHint': 'Download this folder, with everything in it, as one .zip',
  'share.downloadRefused.EMPTY': 'There are no files in this folder to download.',
  'share.downloadRefused.TOO_BIG': 'This folder holds more than one download can — 4 GB. Download the folders inside it one at a time.',
  'share.downloadRefused.TOO_MANY':
    'This folder holds more than one download can — 65,535 folders and files. Download the folders inside it one at a time.',
  'share.downloadBusy': 'Many downloads are going out just now. Try again in a minute.',
  'share.view': 'Open',
  'share.items': 'Items: {n}',
  'share.empty': 'This folder is empty.',
  'share.where': 'Where you are',
  'share.footer': 'Shared from {company} with Viganium Drive. No sign-in is needed.',
  'share.backToStart': 'Back to what was shared',
  'share.unavailable.NOT_FOUND': 'This link does not open anything',
  'share.unavailable.REVOKED': 'This link was stopped',
  'share.unavailable.EXPIRED': 'This link has expired',
  'share.unavailable.GONE': 'What this link shared is no longer available',
  'share.unavailable.ERROR': 'Something went wrong',
  'share.unavailable.hint': 'Ask whoever sent it to you for a new link.',
  'share.unavailable.tryAgain': 'Try again in a moment.',
};

export type TranslationKey = keyof typeof en;

const ar: Record<TranslationKey, string> = {
  'app.name': 'بوابة عملاء فيجانيوم',
  'app.tagline': 'تذاكرك ومهامك وطلبات التغيير',
  'app.surface': 'بوابة العملاء',
  'nav.dashboard': 'لوحة المتابعة',
  'nav.tickets': 'التذاكر',
  'nav.changeRequests': 'طلبات التغيير',
  'nav.complaints': 'الشكاوى',
  'nav.account': 'الحساب',
  'nav.openMenu': 'فتح القائمة',
  'theme.toLight': 'التبديل إلى الوضع الفاتح',
  'theme.toDark': 'التبديل إلى الوضع الداكن',

  'common.search': 'بحث',
  'common.save': 'حفظ',
  'common.cancel': 'إلغاء',
  'common.create': 'إنشاء',
  'common.send': 'إرسال',
  'common.close': 'إغلاق',
  'common.back': 'رجوع',
  'common.loading': 'جارٍ التحميل…',
  'common.noResults': 'لا يوجد ما يُعرض',
  'common.noResultsHint': 'جرّب تبويبًا آخر أو بحثًا آخر.',
  'common.error': 'حدث خطأ ما',
  'common.retry': 'إعادة المحاولة',
  'common.total': 'الإجمالي',
  'common.page': 'صفحة',
  'common.of': 'من',
  'common.previous': 'السابق',
  'common.next': 'التالي',
  'common.all': 'الكل',
  'common.optional': 'اختياري',
  'common.noProject': 'بدون مشروع محدد',
  'common.project': 'المشروع',
  'common.status': 'الحالة',
  'common.priority': 'الأولوية',
  'common.nameTooLong': 'لا يزيد على {max} حرفًا — هذا {count}.',
  'common.created': 'تاريخ الطلب',
  'common.updated': 'آخر تحديث',
  'common.email': 'البريد الإلكتروني',
  'common.mobile': 'الجوال',
  'common.you': 'أنت',
  'common.viganium': 'فيجانيوم',

  'auth.welcome': 'تسجيل الدخول',
  'auth.subtitle': 'تابع تذاكرك ومهام مشاريعك وطلبات التغيير مع فيجانيوم.',
  'auth.usernameLabel': 'اسم المستخدم',
  'auth.passwordLabel': 'كلمة المرور',
  'auth.signIn': 'دخول',
  'auth.signingIn': 'جارٍ تسجيل الدخول…',
  'auth.signOut': 'تسجيل الخروج',
  'auth.forgot': 'نسيت كلمة المرور؟ اطلب من جهة تواصلك في فيجانيوم تعيين كلمة جديدة.',
  'auth.disabled': 'هذا الحساب موقوف. يرجى التواصل مع فيجانيوم.',
  'auth.invalid': 'اسم المستخدم أو كلمة المرور غير صحيحة.',

  'dashboard.title': 'لوحة المتابعة',
  'dashboard.greeting': 'أهلًا بك، {name}',
  'dashboard.subtitle': 'آخر ما وصلت إليه تذاكرك ومهامك وطلبات التغيير.',
  'dashboard.ticketsTotal': 'إجمالي التذاكر',
  'dashboard.ticketsOpen': 'بانتظارنا',
  'dashboard.ticketsInProgress': 'قيد التنفيذ',
  'dashboard.ticketsFinished': 'منتهية',
  'dashboard.tasks': 'مهام مشاريعك',
  'dashboard.tasksHint': 'كل مهام مشاريعك حسب حالتها.',
  'dashboard.tasksNone': 'لا توجد مهام على مشاريعك بعد.',
  'dashboard.projects': 'مشاريعك',
  'dashboard.tasksDone': 'المهام المنجزة',
  'dashboard.team': 'المتصلون الآن',
  'dashboard.teamHint': 'أعضاء فريق مشاريعك الموجودون في ورديتهم الآن.',
  'dashboard.teamEmpty': 'لا أحد من فريق مشاريعك في ورديته الآن.',
  'dashboard.onBreak': 'في استراحة',
  'dashboard.onShiftSince': 'في الوردية منذ {time}',
  'dashboard.changeRequests': 'طلبات التغيير',
  'dashboard.changeRequestsOpen': 'ما زالت مفتوحة',
  'dashboard.seeAll': 'عرض الكل',
  'dashboard.ticketsShut': 'فتح التذاكر من هنا غير متاح بعد. يرجى التواصل مع فيجانيوم.',

  'tickets.title': 'التذاكر',
  'tickets.new': 'تذكرة جديدة',
  'tickets.number': 'التذكرة',
  'tickets.subject': 'الموضوع',
  'tickets.subjectHint': 'ملخص قصير — «صفحة الدخول بطيئة».',
  'tickets.description': 'التفاصيل',
  'tickets.descriptionHint': 'ماذا حدث، وأين، ومنذ متى. لقطات الشاشة والرسائل الصوتية تساعدنا.',
  'tickets.projectHint': 'المشروع الذي تخصّه التذكرة، إن كانت تخص مشروعًا.',
  'tickets.projectHintSupport':
    'المشروع الذي تخصّه التذكرة، إن كانت تخص مشروعًا. لا يظهر هنا المشروع الذي انتهت فترة دعمه: يرجى التواصل مع فيجانيوم لتجديدها.',
  'tickets.projectClosed': 'انتهت فترة الدعم لهذا المشروع، فلا يمكن فتح تذاكر جديدة عليه. يرجى التواصل مع فيجانيوم لتجديدها.',
  'tickets.projectCancelled': 'أُلغي هذا المشروع، فلا يمكن فتح تذاكر جديدة عليه.',
  'dashboard.supportUntil': 'الدعم حتى {date}',
  'tickets.replies': 'الردود',
  'tickets.groupOpen': 'بانتظارنا',
  'tickets.groupInProgress': 'قيد التنفيذ',
  'tickets.groupFinished': 'منتهية',
  'tickets.groupCancelled': 'ملغاة',
  'tickets.raisedByYou': 'فتحتها أنت',
  'tickets.raisedByUs': 'فتحتها فيجانيوم',
  'tickets.raisedByUsFor': 'فتحتها فيجانيوم بطلب من {name}',
  'tickets.raisedBy': 'فتحها',
  'tickets.requestedBy': 'بطلب من',
  'tickets.raisedByName': 'فتحها {name}',
  'tickets.department': 'القسم',
  'tickets.departmentHint': 'فريقنا الذي تصل إليه تذكرتك.',
  'tickets.departmentChoose': '— اختر القسم —',
  'tickets.departmentGone': 'لم يعد هذا القسم يستقبل تذاكرك. يرجى اختيار قسم آخر.',
  'tickets.empty': 'لا توجد تذاكر بعد',
  'tickets.emptyHint': 'افتح تذكرة وسيتولاها فريقنا.',
  'tickets.created': 'تم فتح التذكرة {number} — أُبلغ فريقنا.',
  'tickets.handledBy': 'يتابعها',
  'tickets.notAssigned': 'لم تُستلم بعد',
  'tickets.details': 'التفاصيل',
  'tickets.files': 'الملفات',
  'tickets.conversation': 'المحادثة',
  'tickets.noReplies': 'لا ردود بعد — سيظهر ردّنا هنا.',
  'tickets.answer': 'ردّك',
  'tickets.answerPlaceholder': 'اكتب ردّك…',
  'tickets.waitingOnYou': 'ننتظر ردّك.',
  'tickets.finished': 'انتهت هذه التذكرة. افتح تذكرة جديدة إن احتجت إلى مساعدة أخرى.',
  'tickets.resolution': 'الحل',
  'tickets.sent': 'تم الإرسال.',

  'crs.title': 'طلبات التغيير',
  'crs.new': 'طلب تغيير جديد',
  'crs.number': 'الطلب',
  'crs.titleField': 'العنوان',
  'crs.titleHint': 'ما الذي تريد تغييره، في كلمات قليلة.',
  'crs.details': 'التفاصيل',
  'crs.detailsHint': 'صف التغيير المطلوب. يمكن لملف أو رسالة صوتية أن تقوم بذلك.',
  'crs.projectHint': 'المشروع الذي يخصّه التغيير، إن كان يخص مشروعًا.',
  'crs.empty': 'لا توجد طلبات تغيير بعد',
  'crs.emptyHint': 'اطلب تغييرًا وسيراجعه فريقنا.',
  'crs.created': 'تم إرسال طلب التغيير {number} — سيراجعه فريقنا.',
  'crs.ourNote': 'ملاحظتنا',
  'crs.history': 'السجل',
  'crs.raised': 'أرسلته أنت',
  'crs.raisedByName': 'أرسله {name}',
  'crs.raisedBy': 'أرسله',
  'crs.files': 'الملفات',
  'crs.needsSomething': 'صف التغيير، أو أرفق ملفًا أو رسالة صوتية.',
  'crs.state.NEW': 'بانتظار مراجعتنا.',
  'crs.state.IN_REVIEW': 'نراجعه الآن.',
  'crs.state.APPROVED': 'تمت الموافقة — سننفّذه.',
  'crs.state.REJECTED': 'لن يُنفَّذ — اطّلع على ملاحظتنا.',
  'crs.state.DONE': 'تم التنفيذ.',
  'crs.messages': 'الرسائل',
  'crs.conversation': 'المحادثة',
  'crs.noMessages': 'لا رسائل بعد. اكتب إلينا هنا بشأن هذا التغيير — وتظهر ردودنا هنا أيضًا.',
  'crs.message': 'رسالتك',
  'crs.messagePlaceholder': 'اكتب إلينا بشأن هذا التغيير…',
  'crs.closed': 'تم تنفيذ طلب التغيير هذا. أرسل طلبًا جديدًا إن احتجت إلى شيء آخر.',

  'complaints.title': 'الشكاوى',
  'complaints.subtitle': 'أخبرنا إن خذلك أحد العاملين على مشاريعك.',
  'complaints.new': 'شكوى جديدة',
  'complaints.number': 'الشكوى',
  'complaints.about': 'بشأن',
  'complaints.aboutHint': 'العاملون على مشاريعك.',
  'complaints.find': 'ابحث عن شخص…',
  'complaints.noPeople': 'لا يعمل أحد على مشاريعك بعد، فلا أحد لتختاره.',
  'complaints.noMatch': 'لا أحد بهذا الاسم.',
  'complaints.titleField': 'العنوان',
  'complaints.titleHint': 'ما الذي حدث، في كلمات قليلة.',
  'complaints.details': 'التفاصيل',
  'complaints.detailsHint': 'ما الذي حدث ومتى. يمكن لملف أو رسالة صوتية أن تقوم بذلك.',
  'complaints.needsSomething': 'اذكر ما حدث، أو أرفق ملفًا أو رسالة صوتية.',
  'complaints.private': 'لا يقرأ شكواك إلا إدارة فيجانيوم — ولا يقرؤها الشخص المعني أبدًا.',
  'complaints.empty': 'لا توجد شكاوى',
  'complaints.emptyHint': 'نتمنى أن يبقى الأمر كذلك. إن خذلك أحد العاملين على مشاريعك فأخبرنا هنا.',
  'complaints.created': 'تم إرسال الشكوى {number} — ستنظر فيها إدارتنا.',
  'complaints.ourAnswer': 'ردّنا',
  'complaints.ourNote': 'ملاحظتنا',
  'complaints.history': 'السجل',
  'complaints.made': 'قدّمتها أنت',
  'complaints.madeByName': 'قدّمها {name}',
  'complaints.madeBy': 'قدّمها',
  'complaints.files': 'الملفات',
  'complaints.formerMember': 'شخص لم يعد معنا',
  'complaints.state.NEW': 'بانتظار أن تنظر فيها إدارتنا.',
  'complaints.state.IN_REVIEW': 'تنظر فيها إدارتنا الآن.',
  'complaints.state.CLOSED': 'أُغلقت — اطّلع على ردّنا.',

  'account.title': 'الحساب',
  'account.company': 'الشركة',
  'account.signedInAs': 'الدخول باسم',
  'account.companySignIn': 'حساب الشركة نفسها',
  'account.username': 'اسم المستخدم',
  'account.lastSignIn': 'آخر دخول سابق',
  'account.changePassword': 'تغيير كلمة المرور',
  'account.currentPassword': 'كلمة المرور الحالية',
  'account.newPassword': 'كلمة المرور الجديدة',
  'account.confirmPassword': 'كلمة المرور الجديدة مرة أخرى',
  'account.passwordHint': '8 أحرف على الأقل.',
  'account.mismatch': 'كلمتا المرور الجديدتان غير متطابقتين.',
  'account.changed': 'تم تغيير كلمة المرور. خرجت الأجهزة الأخرى من الحساب.',

  'status.ticket.NEW': 'جديدة',
  'status.ticket.ASSIGNED': 'مُستلمة',
  'status.ticket.IN_PROGRESS': 'قيد التنفيذ',
  'status.ticket.WAITING_REQUESTER': 'بانتظار ردّك',
  'status.ticket.RESOLVED': 'تم الحل',
  'status.ticket.CLOSED': 'مغلقة',
  'status.ticket.REOPENED': 'أُعيد فتحها',
  'status.ticket.CANCELLED': 'ملغاة',
  'status.task.TO_DO': 'قيد الانتظار',
  'status.task.IN_PROGRESS': 'قيد التنفيذ',
  'status.task.BLOCKED': 'متوقفة',
  'status.task.REVIEW': 'قيد المراجعة',
  'status.task.DONE': 'منجزة',
  'status.task.CANCELLED': 'ملغاة',
  'status.cr.NEW': 'جديد',
  'status.cr.IN_REVIEW': 'قيد المراجعة',
  'status.cr.APPROVED': 'معتمد',
  'status.cr.REJECTED': 'مرفوض',
  'status.cr.DONE': 'منجز',
  'status.complaint.NEW': 'جديدة',
  'status.complaint.IN_REVIEW': 'قيد المراجعة',
  'status.complaint.CLOSED': 'مغلقة',
  'status.project.DRAFT': 'قيد التخطيط',
  'status.project.ACTIVE': 'نشط',
  'status.project.TESTING': 'قيد الاختبار',
  'status.project.ON_HOLD': 'متوقف',
  'status.project.SUPPORT': 'قيد الدعم',
  'status.project.COMPLETED': 'مكتمل',
  'status.project.CANCELLED': 'ملغى',
  'status.project.ARCHIVED': 'مؤرشف',
  'priority.LOW': 'منخفضة',
  'priority.MEDIUM': 'متوسطة',
  'priority.HIGH': 'عالية',
  'priority.URGENT': 'عاجلة',

  'attachments.title': 'المرفقات',
  'attachments.none': 'لا توجد ملفات بعد.',
  'attachments.attachFiles': 'إرفاق ملفات',
  'attachments.recordVoice': 'رسالة صوتية',
  'attachments.recordVoiceHint': 'تسجيل رسالة صوتية',
  'attachments.recording': 'جارٍ التسجيل',
  'attachments.startingMic': 'جارٍ تشغيل الميكروفون…',
  'attachments.stopRecording': 'إيقاف وحفظ',
  'attachments.discardRecording': 'تجاهل',
  'attachments.voiceNote': 'رسالة صوتية',
  'attachments.hint': 'صور وملفات PDF وWord وExcel ورسائل صوتية وفيديو — حتى {mb} ميجابايت للملف و{videoMb} ميجابايت للفيديو.',
  'attachments.dropHere': 'أفلت الملفات هنا لإرفاقها',
  'attachments.attach': 'إرفاق',
  'attachments.uploading': 'جارٍ الرفع…',
  'attachments.remove': 'إزالة',
  'attachments.removeConfirm': 'إزالة هذا الملف؟',
  'attachments.keep': 'إبقاء',
  'attachments.removed': 'تمت إزالة الملف.',
  'attachments.added': 'تم إرفاق الملفات.',
  'attachments.download': 'تنزيل',
  'attachments.open': 'فتح',
  'attachments.play': 'تشغيل',
  'attachments.pause': 'إيقاف مؤقت',
  'attachments.speed': 'سرعة التشغيل',
  'attachments.micDenied': 'اسمح للمتصفح باستخدام الميكروفون لتسجيل رسالة صوتية.',
  'attachments.micUnavailable': 'لا يدعم هذا المتصفح أو الجهاز تسجيل الصوت.',
  'attachments.tooLarge': 'أكبر من {mb} ميجابايت.',
  'attachments.typeNotAllowed': 'هذا النوع من الملفات غير مقبول.',
  'attachments.tooMany': 'حتى {n} ملفًا في المرة الواحدة.',
  'attachments.maxLength': 'تتوقف الرسالة الصوتية عند {min} دقائق.',
  'attachments.playFailed': 'لا يمكن تشغيل هذا التسجيل هنا — نزّله بدلًا من ذلك.',
  'attachments.failed': 'تعذّر الرفع.',
  'attachments.waitForUploads': 'انتظر حتى يكتمل الرفع.',
  'attachments.addedBy': 'أضافه {name}',
  'attachments.tooLargeVideo': 'الحد الأقصى للفيديو {mb} ميجابايت.',
  'attachments.videoUnplayable': 'لا يمكن تشغيل هذا الفيديو هنا — نزّله بدلًا من ذلك.',
  'attachments.storageUnreachable': 'تعذّر الوصول إلى مخزن الملفات. حاول مرة أخرى بعد قليل.',
  'attachments.notArrived': 'لم يصل الملف كاملًا. أزله ثم أرفقه من جديد.',

  'notFound.title': 'هذه الصفحة غير موجودة',
  'notFound.hint': 'ربما يكون الرابط قديمًا. ابدأ من لوحة المتابعة.',
  'nav.calls': 'المكالمات',
  'calls.title': 'المكالمات',
  'calls.subtitle': 'اتصل بأعضاء فريق فيجانيوم المختارين لك. كل مكالمة مسجّلة.',
  'calls.unavailable': 'المكالمات غير متاحة الآن. يرجى المحاولة لاحقًا.',
  'calls.whoYouCanCall': 'من يمكنك الاتصال به',
  'calls.whoYouCanCallHint': 'أعضاء فريق فيجانيوم المختارون لك، وهل كلٌّ منهم يعمل الآن.',
  'calls.nobody': 'لا أحد للاتصال به بعد',
  'calls.nobodyHint': 'لم تختر فيجانيوم أحدًا لتتصل به بعد.',
  'calls.recordedNotice': 'هذه المكالمة مسجّلة',
  'calls.recordingNow': 'هذه المكالمة قيد التسجيل',
  'calls.recordingBadge': 'تسجيل',
  'calls.person.working': 'يعمل الآن',
  'calls.person.onBreak': 'في استراحة',
  'calls.person.notWorking': 'لا يعمل الآن',
  'calls.person.onCall': 'في مكالمة أخرى',
  'calls.call': 'اتصال',
  'calls.callPerson': 'الاتصال بـ{name}',
  'calls.yourCalls': 'مكالماتك',
  'calls.yourCallsHint': 'كل مكالمة أُجريت من حسابات شركتك.',
  'calls.filter.all': 'الكل',
  'calls.filter.answered': 'تم الرد',
  'calls.filter.unanswered': 'لم يُرد عليها',
  'calls.noneYet': 'لا توجد مكالمات بعد',
  'calls.noneYetHint': 'تظهر مكالماتك هنا بعد أن تجري واحدة.',
  'calls.with': 'مع',
  'calls.outcome': 'النتيجة',
  'calls.madeBy': 'المتصل',
  'calls.when': 'الوقت',
  'calls.duration': 'المدة',
  'calls.voiceCall': 'مكالمة صوتية',
  'calls.groupCall': 'مكالمة جماعية',
  'calls.loadMore': 'تحميل المزيد',
  'calls.you': 'أنت',
  'calls.notFound': 'لا توجد مكالمة كهذه.',
  'calls.people': 'الأشخاص',
  'calls.peopleHint': 'من تم الاتصال به، ومن كان في المكالمة.',
  'calls.wasOnCall': 'في المكالمة',
  'calls.wasRecorded': 'سجّلت فيجانيوم هذه المكالمة.',
  'calls.details': 'التفاصيل',
  'calls.status': 'الحالة',
  'calls.madeAt': 'وقت الاتصال',
  'calls.started': 'بدأت',
  'calls.ended': 'انتهت',
  'calls.why': 'سبب الانتهاء',
  'calls.addPeople': 'إضافة أشخاص',
  'calls.addPeopleSubtitle': 'يُدعون إلى المكالمة نفسها — والتسجيل نفسه.',
  'calls.whoToAdd': 'من تريد إضافته',
  'calls.add': 'إضافة إلى المكالمة',
  'calls.added': 'تمت دعوتهم إلى المكالمة.',
  'calls.notInCall': 'لست في مكالمة.',
  'calls.nobodyToAdd': 'كل من يمكنك الاتصال به موجود في هذه المكالمة.',
  'calls.upTo': 'حتى {n} أشخاص آخرين.',
  'calls.currentCall': 'مكالمتك',
  'calls.move': 'تحريك المكالمة',
  'calls.moveHint': 'اسحبها إلى أي مكان أو استخدم مفاتيح الأسهم. النقر المزدوج يعيدها إلى الزاوية.',
  'calls.state.calling': 'جارٍ الاتصال…',
  'calls.state.preparing': 'جارٍ بدء التسجيل…',
  'calls.state.connecting': 'جارٍ الاتصال بالمكالمة…',
  'calls.state.reconnecting': 'جارٍ إعادة الاتصال…',
  'calls.state.ending': 'جارٍ الإنهاء…',
  'calls.state.otherDevice': 'أُجريت من تبويب أو جهاز آخر',
  'calls.peopleCount': '{n} أشخاص',
  'calls.endingSoon': 'تنتهي هذه المكالمة بعد 5 دقائق — أطول مدة لمكالمة.',
  'calls.micDenied': 'متصفحك يحجب الميكروفون: اسمح به من شريط العنوان ليُسمع صوتك.',
  'calls.enableAudio': 'انقر لسماع المكالمة',
  'calls.inCall': 'في المكالمة',
  'calls.joining': 'جارٍ الانضمام…',
  'calls.mute': 'كتم',
  'calls.unmute': 'إلغاء الكتم',
  'calls.share.start': 'مشاركة شاشتك',
  'calls.share.stop': 'إيقاف مشاركة شاشتك',
  'calls.share.yours': 'شاشتك قيد المشاركة',
  'calls.share.failed': 'تعذّرت مشاركة الشاشة. يمكن المحاولة مجددًا أو اختيار نافذة أو علامة تبويب أخرى.',
  'calls.screen.label': 'الشاشات المشتركة',
  'calls.screen.of': 'شاشة {name}',
  'calls.screen.show': 'عرض شاشة {name}',
  'calls.screen.smaller': 'تصغير',
  'calls.screen.larger': 'تكبير',
  'calls.screen.full': 'ملء الشاشة',
  'calls.screen.exitFull': 'الخروج من ملء الشاشة',
  'calls.screen.hide': 'إخفاء',
  'calls.screen.float': 'عرضها فوق النوافذ الأخرى',
  'calls.screen.move': 'تحريك الشاشة المشتركة',
  'calls.screen.moveHint': 'اسحبها إلى أي مكان من أعلاها أو من الصورة، أو استخدم مفاتيح الأسهم. النقر المزدوج يعيدها إلى مكانها.',
  'calls.screen.resize': 'تغيير حجم الشاشة المشتركة',
  'calls.screen.resizeHint': 'اسحب هذه الزاوية، أو استخدم مفاتيح الأسهم.',
  'calls.screen.notRecorded': 'يُسجَّل صوت المكالمة، ولا تُسجَّل الشاشة المشتركة.',
  'calls.screen.waiting': 'بانتظار الصورة…',
  'calls.hangUp': 'إنهاء',
  'calls.cancel': 'إلغاء',
  'calls.openCall': 'فتح المكالمة',
  'calls.outcome.LIVE': 'جارية',
  'calls.outcome.ANSWERED': 'تم الرد',
  'calls.outcome.NO_ANSWER': 'لا رد',
  'calls.outcome.MISSED': 'فائتة',
  'calls.outcome.DECLINED': 'مرفوضة',
  'calls.outcome.BUSY': 'مشغول',
  'calls.outcome.CANCELLED': 'ملغاة',
  'calls.outcome.FAILED': 'فشلت',
  'calls.participant.RINGING': 'يرنّ',
  'calls.participant.ACCEPTED': 'رد',
  'calls.participant.DECLINED': 'رفض',
  'calls.participant.MISSED': 'لا رد',
  'calls.participant.BUSY': 'مشغول',
  'calls.participant.CANCELLED': 'أُلغيت الدعوة',
  'calls.participant.LEFT': 'غادر',
  'calls.callStatus.RINGING': 'ترنّ',
  'calls.callStatus.PREPARING': 'تبدأ',
  'calls.callStatus.ACTIVE': 'جارية',
  'calls.callStatus.ENDING': 'تنتهي',
  'calls.callStatus.COMPLETED': 'مكتملة',
  'calls.callStatus.MISSED': 'لا رد',
  'calls.callStatus.REJECTED': 'مرفوضة',
  'calls.callStatus.CANCELLED': 'ملغاة',
  'calls.callStatus.BUSY': 'مشغول',
  'calls.callStatus.FAILED': 'فشلت',
  'calls.ended.normal': 'انتهت المكالمة.',
  'calls.ended.noAnswer': 'لم يرد أحد على المكالمة.',
  'calls.ended.declined': 'رُفضت المكالمة.',
  'calls.ended.busy': 'إنهم في مكالمة أخرى. يرجى المحاولة لاحقًا.',
  'calls.ended.cancelled': 'أُلغيت المكالمة.',
  'calls.ended.recordingNotStarted': 'تعذّر بدء المكالمة: لم يبدأ تسجيلها، ولا تستمر مكالمة بلا تسجيل.',
  'calls.ended.recordingLost': 'انتهت المكالمة لأنه تعذّر تأكيد تسجيلها.',
  'calls.ended.maxDuration': 'بلغت المكالمة أطول مدة مسموح بها.',
  'calls.ended.lostServer': 'انقطع الاتصال بفيجانيوم فأُنهيت المكالمة: تعذّر تأكيد تسجيلها.',
  'calls.ended.lostServerRinging': 'انقطع الاتصال بفيجانيوم والمكالمة ترنّ، فأُلغيت.',
  'calls.error.micBlocked': 'متصفحك يحجب الميكروفون. اسمح به من شريط العنوان ثم اتصل مجددًا.',
  'calls.error.micMissing': 'تعذّر فتح أي ميكروفون. صِل ميكروفونًا أو أغلق ما يستخدمه ثم اتصل مجددًا.',
  'calls.error.unavailable': 'المكالمات غير متاحة الآن. يرجى المحاولة لاحقًا.',
  'calls.error.alreadyInCall': 'أنت في مكالمة بالفعل. أنهِها أولًا.',
  'calls.error.full': 'تتسع المكالمة لـ{n} أشخاص على الأكثر.',
  'calls.error.otherDevice': 'أُجريت هذه المكالمة من تبويب أو جهاز آخر.',
  'calls.error.notCallable': 'يمكنك الاتصال فقط بالأشخاص الذين اختارتهم لك فيجانيوم.',
  'calls.error.notAvailable': 'لا يستطيع هذا الشخص تلقي المكالمات الآن.',
  // A Viganium Drive link's page (2026-11-02).
  'share.kicker': 'شُورك معك',
  'share.from': 'شاركه معك {name} من {company}.',
  'share.forYou': 'إلى {name}',
  'share.until': 'متاح حتى {date}',
  'share.download': 'تنزيل',
  'share.downloadAll': 'تنزيل الكل',
  'share.downloadAllHint': 'تنزيل هذا المجلد بكل ما فيه في ملف ‎.zip واحد',
  'share.downloadRefused.EMPTY': 'لا توجد ملفات في هذا المجلد لتنزيلها.',
  'share.downloadRefused.TOO_BIG': 'يحوي هذا المجلد أكثر مما يتسع له تنزيل واحد — 4 جيجابايت. نزّل المجلدات التي فيه واحدًا تلو الآخر.',
  'share.downloadRefused.TOO_MANY':
    'يحوي هذا المجلد أكثر مما يتسع له تنزيل واحد — 65,535 من المجلدات والملفات. نزّل المجلدات التي فيه واحدًا تلو الآخر.',
  'share.downloadBusy': 'هناك تنزيلات كثيرة جارية الآن. حاول مرة أخرى بعد دقيقة.',
  'share.view': 'فتح',
  'share.items': 'العناصر: {n}',
  'share.empty': 'هذا المجلد فارغ.',
  'share.where': 'موقعك',
  'share.footer': 'شُورك من {company} عبر فيجانيوم درايف، دون الحاجة إلى تسجيل الدخول.',
  'share.backToStart': 'العودة إلى ما شُورك',
  'share.unavailable.NOT_FOUND': 'هذا الرابط لا يفتح شيئًا',
  'share.unavailable.REVOKED': 'أُوقف هذا الرابط',
  'share.unavailable.EXPIRED': 'انتهت صلاحية هذا الرابط',
  'share.unavailable.GONE': 'لم يعد ما شاركه هذا الرابط متاحًا',
  'share.unavailable.ERROR': 'حدث خطأ ما',
  'share.unavailable.hint': 'اطلب رابطًا جديدًا ممن أرسله إليك.',
  'share.unavailable.tryAgain': 'حاول مرة أخرى بعد لحظات.',
};

const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = { en, ar };

interface I18nContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  /** Picks the Arabic field when the page is Arabic and one exists. */
  localized: (
    row:
      | { name?: string | null; nameAr?: string | null; fullName?: string; fullNameAr?: string | null }
      | null
      | undefined,
  ) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const LOCALE_KEY = 'viganium.portal.locale';

function readLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(LOCALE_KEY);
    if (stored === 'en' || stored === 'ar') return stored;
  } catch {
    /* storage blocked — fall back to the browser's language */
  }
  return navigator.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readLocale);
  const dir: 'ltr' | 'rtl' = locale === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    try {
      window.localStorage.setItem(LOCALE_KEY, locale);
    } catch {
      /* the choice just isn't remembered */
    }
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      let text: string = DICTIONARIES[locale][key] ?? en[key] ?? key;
      if (vars) for (const [k, v] of Object.entries(vars)) text = text.replace(`{${k}}`, String(v));
      return text;
    },
    [locale],
  );

  const localized = useCallback<I18nContextValue['localized']>(
    (row) => {
      if (!row) return '—';
      if (locale === 'ar') return row.nameAr || row.fullNameAr || row.name || row.fullName || '—';
      return row.name || row.fullName || '—';
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, dir, setLocale, t, localized }), [locale, dir, setLocale, t, localized]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>.');
  return ctx;
}

/** A status or priority, in the reader's language. */
export function statusLabel(
  t: I18nContextValue['t'],
  kind: 'ticket' | 'task' | 'cr' | 'complaint' | 'project',
  value: string,
): string {
  return t(`status.${kind}.${value}` as TranslationKey);
}

export function priorityLabel(t: I18nContextValue['t'], value: string): string {
  return t(`priority.${value}` as TranslationKey);
}
